import { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LayoutDashboard, Trophy, CalendarDays, Newspaper, Users, FileText, Sparkles, Gift, ArrowLeft, Clock, CheckCircle2, PauseCircle, XCircle, Facebook, Share2, PlusCircle, HeartHandshake, SmartphoneNfc } from "lucide-react";

const LINKS = [
  { to: "/espace-club", end: true, icon: LayoutDashboard, label: "Tableau de bord", testid: "club-nav-dashboard" },
  { to: "/espace-club/assistant-ia", icon: Sparkles, label: "Mon Agent IA", testid: "club-nav-ai", highlight: true },
  { to: "/ma-carte", icon: SmartphoneNfc, label: "Ma carte de visite digitale", testid: "club-nav-carte" },
  { to: "/espace-club/profil", icon: Trophy, label: "Ma page vitrine", testid: "club-nav-profile" },
  { to: "/espace-club/evenements", icon: CalendarDays, label: "Mes événements", testid: "club-nav-events" },
  { to: "/espace-club/actualites", icon: Newspaper, label: "Mes actualités", testid: "club-nav-news" },
  { to: "/petites-annonces/deposer", icon: PlusCircle, label: "Déposer une petite annonce", testid: "club-nav-new-annonce" },
  { to: "/entre-voisins", icon: HeartHandshake, label: "Publier une entraide / un service", testid: "club-nav-new-voisin" },
  { to: "/demande-de-devis", icon: FileText, label: "Faire une demande de devis", testid: "club-nav-new-quote" },
  { to: "/espace-club/publications-facebook", icon: Facebook, label: "Publier sur Facebook", testid: "club-nav-fbposts" },
  { to: "/espace-club/partager-evenement", icon: Share2, label: "Partager un événement", testid: "club-nav-event-share" },
  { to: "/espace-club/partager-article", icon: Newspaper, label: "Partager un article de blog", testid: "club-nav-blog-share" },
  { to: "/espace-club/partager-actualite", icon: Newspaper, label: "Partager un article d'actualité", testid: "club-nav-press-share" },
  { to: "/espace-club/partager-entre-voisins", icon: Share2, label: "Partager une entraide Entre Voisins", testid: "club-nav-voisin-share" },
  { to: "/espace-club/membres", icon: Users, label: "Mes membres", testid: "club-nav-members" },
  { to: "/espace-club/dons", icon: Gift, label: "Mes dons", testid: "club-nav-donations" },
  { to: "/espace-club/documents", icon: FileText, label: "Mes documents", testid: "club-nav-documents" },
  { to: "/espace-club/ressources", icon: Sparkles, label: "Ressources par activité", testid: "club-nav-resources" },
];

export default function ClubLayout() {
  const [club, setClub] = useState(null);
  useEffect(() => { api.get("/clubs/me").then((r) => setClub(r.data.club)).catch(() => {}); }, []);
  const link = ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm leading-tight ${isActive ? "bg-indigo-700 text-white" : "hover:bg-slate-100 text-slate-700"}`;
  const statusMeta = (() => {
    if (!club) return null;
    if (club.status === "active") return { Icon: CheckCircle2, label: "Page publiée", cls: "text-indigo-700" };
    if (club.status === "suspended") return { Icon: PauseCircle, label: "Page suspendue", cls: "text-slate-500" };
    if (club.status === "rejected") return { Icon: XCircle, label: "Demande refusée", cls: "text-rose-600" };
    return { Icon: Clock, label: "En attente de validation", cls: "text-amber-600" };
  })();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-white border-b md:border-b-0 md:border-r border-slate-200 p-4 md:flex md:flex-col shrink-0">
        <Link to="/" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-4 md:mb-6"><ArrowLeft size={14} /> Retour au site</Link>
        <div className="font-heading font-extrabold text-lg mb-1 truncate">{club?.name || "Mon club"}</div>
        {statusMeta && (
          <div className={`text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-1 ${statusMeta.cls}`} data-testid="club-status-label">
            <statusMeta.Icon size={12} /> {statusMeta.label}
          </div>
        )}
        <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testid}
              className={l.highlight
                ? ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${isActive ? "bg-[#E05638] text-white" : "text-[#E05638] hover:bg-orange-50"}`
                : link}>
              <l.icon size={16} className="shrink-0" /> {l.label}
            </NavLink>
          ))}
        </nav>
        {club?.status === "active" && club?.slug && (
          <a href={`/clubs/${club.slug}`} target="_blank" rel="noopener noreferrer"
             className="mt-4 hidden md:inline-block text-xs text-indigo-700 font-semibold hover:underline" data-testid="club-view-public">
            Voir ma page publique →
          </a>
        )}
      </aside>
      <main className="flex-1 min-w-0 p-4 md:p-10 overflow-x-auto"><Outlet context={{ club, setClub }} /></main>
    </div>
  );
}
